import { EVENT_TOGGLE_SOUND, GAME_SESSION_TIME, EVENT_FOCUS_IN, EVENT_FOCUS_OUT, EVENT_TOGGLE_DRAW, EVENT_TIME_CONTROL } from '@playgame/constants'
import { computed, onScopeDispose, usePlaygameState, usePlaygameView, usePlaygameEventTrigger, navigateTo, useNuxtData, useRealityCheck, useDeposit } from '#imports'

export const useStateActions = () => {
    // `_writableState` is the private writable flags ref; this composable is the sole owner of flag mutations.
    const { _writableState: state, gamesArray, chatCookie, getMagnetGames, message, getGame } = usePlaygameState()
    const { eventTrigger } = usePlaygameEventTrigger()
    const { isMobileView } = usePlaygameView()
    const { showRealityCheck } = useRealityCheck()
    const { data: dialogsData } = useNuxtData('notification:dialogs')
    const { requestDataObject } = useDeposit()

    // One interval per game — plain setInterval so it works when called outside setup,
    // with onScopeDispose ensuring all timers are cleared when the component unmounts.
    const sessionTimers = new Map<string, ReturnType<typeof setInterval>>()
    onScopeDispose(() => {
        for (const timerId of sessionTimers.values()) clearInterval(timerId)
        sessionTimers.clear()
    })

    const toggleSound = (enabled: boolean) => {
        state.value.sound = enabled !== undefined ? enabled : !state.value.sound

        eventTrigger(state.value.focusedGame, EVENT_TOGGLE_SOUND, { enabled: state.value.sound })
    }

    const setFullscreenState = (value: boolean) => {
        state.value.fullScreen = value
    }

    const setChatCanLoad = () => {
        state.value.canLoadChat = true
    }

    const toggleChat = (active: boolean | undefined) => {
        if (!state.value.canLoadChat) return

        state.value.chat = active !== undefined ? active : !state.value.chat

        // Set chat cookie (Desktop only)
        if (!isMobileView.value) {
            chatCookie.value = state.value.chat
        }
    }

    const toggleChatMinimized = (active?: boolean) => {
        state.value.chatMinimized = active !== undefined ? active : !state.value.chatMinimized
    }

    const togglePayments = async (active?: boolean) => {
        // A deposit redirect unmounts the dialog, firing @close → togglePayments(false);
        // skip it so we don't push ?new=1 onto the outgoing redirect URL.
        if (requestDataObject.value) return

        state.value.menuVisible = false // Hide menu when payments are toggled
        state.value.paymentsVisible = active !== undefined ? active : !state.value.paymentsVisible

        if (!state.value.paymentsVisible) {
            await navigateTo({ query: { new: gamesArray.value.length === 0 ? '1' : undefined } })
        }
    }

    const toggleMenu = (active?: boolean) => {
        state.value.paymentsVisible = false // Hide payments when menu is toggled
        state.value.menuVisible = active !== undefined ? active : !state.value.menuVisible
    }

    const toggleBalanceDropdown = (active?: boolean, onOpen?: () => void) => {
        state.value.balanceDropdownVisible = active !== undefined ? active : !state.value.balanceDropdownVisible

        if (state.value.balanceDropdownVisible && onOpen) {
            onOpen()
        }
    }

    const isAnyOverlayVisible = computed(() => {
        if (state.value.paymentsVisible) return true
        if (state.value.menuVisible) return true
        if (state.value.balanceDropdownVisible) return true
        if (message.value) return true

        if (dialogsData.value?.notifications?.some(
            (n: any) => n.presentation_type === 'dialog' || n.presentation_type === 'persistent_dialog'
        )) return true

        if (showRealityCheck.value) return true

        return false
    })

    const toggleGameFocus = (focused: boolean) => {
        eventTrigger(state.value.focusedGame, focused ? EVENT_FOCUS_IN : EVENT_FOCUS_OUT, { enabled: focused })
        eventTrigger(state.value.focusedGame, EVENT_TIME_CONTROL, { running: focused })

        // Send focusOut event to non-focused games - this mutes them but allows their notification sounds to play
        getMagnetGames.value?.forEach((game) => {
            if (game?.identifier !== state.value.focusedGame) {
                eventTrigger(game?.identifier, EVENT_FOCUS_OUT, { enabled: false })
            }
        })

        eventTrigger(state.value.focusedGame, EVENT_TOGGLE_DRAW, { enabled: focused })
    }

    const startGameSessionTime = (identifier: string) => {
        if (sessionTimers.has(identifier)) return

        const game = getGame(identifier)
        if (!game) return

        const timerId = setInterval(() => {
            const currentGame = getGame(identifier)
            if (currentGame) {
                currentGame[GAME_SESSION_TIME] = currentGame.gameSessionTime + 1
            }
        }, 1_000)
        sessionTimers.set(identifier, timerId)
    }

    const setSound = (value: boolean) => {
        state.value.sound = value
    }

    const setGameStarting = (value: boolean) => {
        state.value.gameStarting = value
    }

    const setGameHasFreeSpins = (value: boolean) => {
        state.value.gameHasFreeSpins = value
    }

    const setFocusedGameId = (gameId: string) => {
        state.value.focusedGame = gameId
    }

    return {
        toggleSound,
        setFullscreenState,
        toggleChat,
        toggleChatMinimized,
        togglePayments,
        toggleMenu,
        toggleBalanceDropdown,
        toggleGameFocus,
        startGameSessionTime,
        setChatCanLoad,
        isAnyOverlayVisible,
        setSound,
        setGameStarting,
        setGameHasFreeSpins,
        setFocusedGameId
    }
}
